import { Box, Button, Container } from '@mui/material'
import { OrderResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { HeaderWithActions, InitializationWrapper } from '@planning/components'
import { useGeneralCargoLocations } from '@planning/hooks/useGeneralCargoLocations'
import { orderService } from '@planning/services'
import { GetOrderDamagesAmountFunc } from '@planning/stores/generalCargo/OrderDamageDto'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router'
import { GeneralCargoOrderLocationItem } from './Components/GeneralCargoOrderLocationItem'
import { GeneralCargoOrdersTable } from './Components/GeneralCargoOrdersTable'

export interface IGeneralCargoPageProps {
  onOpenReservation?: (order: OrderResponseDto) => void
  getOrderReservationsFunc?: (
    orderIds: number[],
  ) => Promise<Array<{ orderId: number; planned: GeneralCargoOrderLocationItem[] }>>
  reservationUpdate?: { orderId: number; reservations: GeneralCargoOrderLocationItem[] }
  getOrderDamagesAmountFunc?: GetOrderDamagesAmountFunc
  getOrderLocationsFunc?: (
    orderIds: number[],
  ) => Promise<Array<{ orderId: number; locations: GeneralCargoOrderLocationItem[] }>>
}

export const GeneralCargoPage: FC<IGeneralCargoPageProps> = observer(
  ({
    onOpenReservation,
    getOrderDamagesAmountFunc,
    getOrderLocationsFunc,
    getOrderReservationsFunc,
    reservationUpdate,
  }: IGeneralCargoPageProps) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const navigate = useNavigate()
    const { generalCargoViewStore, appViewStore, companyItemStore } = usePlanningStore()

    generalCargoViewStore.getOrderDamagesAmountByOrderIds = getOrderDamagesAmountFunc

    const { loading } = useAsyncFetch(async () => {
      await generalCargoViewStore.getPackages()
      await generalCargoViewStore.loadAllOrders()
    }, [generalCargoViewStore])

    const { orderLocationsById, orderReservationsById } = useGeneralCargoLocations(
      generalCargoViewStore.generalCargoOrdersStore.items,
      getOrderLocationsFunc,
      getOrderReservationsFunc,
      reservationUpdate,
    )

    const onFormDialogOpen = (order?: OrderResponseDto) => {
      generalCargoViewStore.selectOrder(order)

      if (order) {
        navigate(`${PATHS.generalCargo.orders.url}/${order.id}`)
      } else {
        navigate(`${PATHS.generalCargo.orders.url}/new`)
      }
    }

    const onDeleteOrder = async (order: OrderResponseDto) => {
      try {
        const confirmed = await appViewStore.setOpenConfirmDialog(
          true,
          t('doYouWantToDeleteOrder', 'Do you want to delete order?'),
        )
        if (confirmed) {
          await orderService.delete(order.id, true)
          generalCargoViewStore.loadAllOrders()
          appViewStore.setShowAlert(
            'success',
            t('orderDeletedSuccessfully', 'Order deleted successfully'),
          )
        }
      } catch {
        appViewStore.setShowAlert('error', t('failedToDelete', 'Failed to delete'))
      }
    }

    const customerNamesById = Object.values(companyItemStore.elements).reduce(
      (acc, company) => {
        acc[company.data.id] = company.data.name
        return acc
      },
      {} as Record<number, string>,
    )

    const customerNamesByReferenceId = Object.values(companyItemStore.elements).reduce(
      (acc, company) => {
        acc[company.data.referenceId] = company.data.name
        return acc
      },
      {} as Record<number, string>,
    )

    const packageNamesById = generalCargoViewStore.packages.reduce(
      (acc, packageDto) => {
        acc[packageDto.id] = packageDto.name
        return acc
      },
      {} as Record<number, string>,
    )
    const damageAmountsByOrderId = generalCargoViewStore.damageOrders.reduce(
      (acc, damageOrder) => {
        acc[damageOrder.orderId] = damageOrder.amount
        return acc
      },
      {} as Record<number, number>,
    )

    return (
      <InitializationWrapper isInitializing={loading}>
        <Box sx={{ backgroundColor: theme.palette.grey[100], minHeight: '100vh' }}>
          <Helmet>
            <title>{t('generalCargoOrders', 'General cargo orders')}</title>
          </Helmet>
          <HeaderWithActions
            title={t('generalCargoOrders', 'General cargo orders')}
            noMarginBottom
            actions={[
              <Button key='addOrderButton' variant='contained' onClick={() => onFormDialogOpen()}>
                {t('createOrder', 'Create order')}
              </Button>,
            ]}
          />
          <Container maxWidth='xl' sx={{ paddingY: 2 }}>
            <GeneralCargoOrdersTable
              orders={generalCargoViewStore.generalCargoOrdersStore.items}
              orderLocationsById={orderLocationsById}
              orderReservationsById={orderReservationsById}
              damageAmountsByOrderId={damageAmountsByOrderId}
              customerNamesById={customerNamesById}
              customerNamesByReferenceId={customerNamesByReferenceId}
              packageNamesById={packageNamesById}
              onEdit={onFormDialogOpen}
              onOpenReservation={onOpenReservation}
              onDelete={onDeleteOrder}
            />
          </Container>
        </Box>
      </InitializationWrapper>
    )
  },
)
